import React from "react";
import images from "../../../../utilities/images/images";
import "./EngageProfile.css";

function EngageProfile({
  avatarSrc = images.dummy_user_img,
  name = "Rohit Sharma",
  subtitle = "JEE • Class 11 • Anushaasan Live",
  tierLabel = "",
  showCheckmarkBadge = false,
  dailyGoalLabel = "Your Daily Goal",
  dailyGoalValue = "03/15",
  dailyGoalSuffix = "Solved",
  xpLabel = "XP Points (Total)",
  xpValue = "2.5k",
  xpSuffix = "points",
  onDailyGoalClick,
  onInfoClick,
  onProfileImageFileSelected,
  isProfileImageUploading = false,
}) {
  const tierTrimmed = String(tierLabel ?? "").trim();
  const isElite = tierTrimmed.toLowerCase() === "elite";

  const [isPhotoMenuOpen, setIsPhotoMenuOpen] = React.useState(false);
  const [isWebcamOpen, setIsWebcamOpen] = React.useState(false);
  const [webcamError, setWebcamError] = React.useState("");
  const avatarAreaRef = React.useRef(null);
  const galleryInputRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);

  const closeWebcam = React.useCallback(() => {
    setIsWebcamOpen(false);
    setWebcamError("");
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  React.useEffect(() => {
    if (!isPhotoMenuOpen) return;
    const onDocMouseDown = (ev) => {
      if (avatarAreaRef.current && !avatarAreaRef.current.contains(ev.target)) {
        setIsPhotoMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [isPhotoMenuOpen]);

  React.useEffect(() => {
    if (!isWebcamOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeWebcam();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isWebcamOpen, closeWebcam]);

  React.useEffect(() => {
    if (!isWebcamOpen) return;
    let cancelled = false;
    setWebcamError("");

    const videoElAtMount = videoRef.current;

    if (!navigator.mediaDevices?.getUserMedia) {
      setWebcamError("Camera is not available in this browser or context. Use Upload from device instead.");
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "user" },
            audio: false,
          });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
        }
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const el = videoRef.current;
        if (el) {
          el.srcObject = stream;
        }
      } catch {
        if (!cancelled) {
          setWebcamError("Could not open camera. Check permissions or use Upload from device.");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoElAtMount) {
        videoElAtMount.srcObject = null;
      }
    };
  }, [isWebcamOpen]);

  const handleGalleryFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    setIsPhotoMenuOpen(false);
    if (file && typeof onProfileImageFileSelected === "function") {
      onProfileImageFileSelected(file);
    }
  };

  const handleOpenCamera = () => {
    setIsPhotoMenuOpen(false);
    setIsWebcamOpen(true);
  };

  const handleCaptureWebcam = () => {
    const video = videoRef.current;
    if (!video?.videoWidth || webcamError) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], "profile-camera.jpg", { type: "image/jpeg" });
        closeWebcam();
        if (typeof onProfileImageFileSelected === "function") {
          onProfileImageFileSelected(file);
        }
      },
      "image/jpeg",
      0.92
    );
  };

  return (
    <section className="engage-profile" aria-label="Engage profile">
      <div className="engage-profile__left">
        <div className="engage-profile__avatar-area" ref={avatarAreaRef}>
          <img
            className="engage-profile__avatar"
            src={avatarSrc}
            alt=""
            aria-hidden="true"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = images.dummy_user_img;
            }}
          />
          {typeof onProfileImageFileSelected === "function" ? (
            <>
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                className="engage-profile__hidden-file-input"
                aria-hidden="true"
                tabIndex={-1}
                onChange={handleGalleryFileChange}
              />
              <div className="engage-profile__avatar-edit-wrap">
                <button
                  type="button"
                  className="engage-profile__avatar-edit-btn"
                  aria-label="Change profile photo"
                  aria-haspopup="menu"
                  aria-expanded={isPhotoMenuOpen}
                  disabled={isProfileImageUploading}
                  onClick={() => setIsPhotoMenuOpen((open) => !open)}
                >
                  <img
                    src={images.engage_editgoal_icon}
                    alt=""
                    className="engage-profile__avatar-edit-img"
                    width={14}
                    height={14}
                    aria-hidden="true"
                  />
                </button>
                {isPhotoMenuOpen ? (
                  <div className="engage-profile__photo-menu" aria-label="Profile photo options">
                    <div className="engage-profile__photo-menu-body" role="menu">
                      <div className="engage-profile__photo-menu-camera-row">
                        <button
                          type="button"
                          className="engage-profile__photo-menu-item engage-profile__photo-menu-item--camera"
                          role="menuitem"
                          onClick={handleOpenCamera}
                        >
                          Camera
                        </button>
                        <button
                          type="button"
                          className="engage-profile__photo-menu-close"
                          aria-label="Close menu"
                          onClick={() => setIsPhotoMenuOpen(false)}
                        >
                          <span aria-hidden="true">×</span>
                        </button>
                      </div>
                      <button
                        type="button"
                        className="engage-profile__photo-menu-item"
                        role="menuitem"
                        onClick={() => {
                          setIsPhotoMenuOpen(false);
                          galleryInputRef.current?.click();
                        }}
                      >
                        Upload from device
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>

        <div className="engage-profile__identity">
          <div className="engage-profile__name-row">
            <h3 className="engage-profile__name">{name}</h3>
            {showCheckmarkBadge ? (
              <img
                className="engage-profile__checkmark-badge"
                src={images.engage_checkmark_badge}
                alt=""
                width={22}
                height={22}
                aria-hidden="true"
              />
            ) : null}
          </div>
          {subtitle ? <p className="engage-profile__subtitle">{subtitle}</p> : null}

          {isElite ? (
            <span className="engage-profile__elite-badge" aria-label="Tier: Elite">
              <img
                className="engage-profile__elite-badge__coin"
                src={images.engage_elite_gold}
                alt=""
                width={22}
                height={22}
                aria-hidden="true"
              />
              <span className="engage-profile__elite-badge__text">ELITE</span>
            </span>
          ) : tierTrimmed ? (
            <span className="engage-profile__tier" aria-label={`Tier: ${tierTrimmed}`}>
              {tierTrimmed}
            </span>
          ) : null}
        </div>
      </div>

      <div className="engage-profile__right" aria-label="Stats">
        <div className="engage-profile__stat">
          <div className="engage-profile__stat-head">
            <span className="engage-profile__stat-label">{dailyGoalLabel}</span>
            <button
              type="button"
              className="engage-profile__info-btn"
              aria-label="Open daily goal settings"
              onClick={onDailyGoalClick}
            >
              <img src={images.engage_editgoal_icon} alt="" className="engage-profile__info-img" aria-hidden="true" />
            </button>
          </div>
          <div className="engage-profile__stat-value">
            <span className="engage-profile__stat-main">{dailyGoalValue}</span>
            <span className="engage-profile__stat-sub">{dailyGoalSuffix}</span>
          </div>
        </div>

        <div className="engage-profile__stat">
          <div className="engage-profile__stat-head">
            <span className="engage-profile__stat-label">{xpLabel}</span>
            <button type="button" className="engage-profile__info-btn" onClick={onInfoClick} aria-label="Open details">
              <img src={images.info} alt="" className="engage-profile__info-img" aria-hidden="true" />
            </button>
          </div>
          <div className="engage-profile__stat-value">
            <span className="engage-profile__stat-main">{xpValue}</span>
            <span className="engage-profile__stat-sub">{xpSuffix}</span>
          </div>
        </div>
      </div>

      {isWebcamOpen ? (
        <div
          className="engage-profile__webcam-overlay"
          role="presentation"
          onClick={closeWebcam}
        >
          <div
            className="engage-profile__webcam-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="engage-profile-webcam-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="engage-profile-webcam-title" className="engage-profile__webcam-title">
              Take a photo
            </h4>
            <div className="engage-profile__webcam-video-wrap">
              <video ref={videoRef} className="engage-profile__webcam-video" autoPlay playsInline muted />
            </div>
            {webcamError ? <p className="engage-profile__webcam-error">{webcamError}</p> : null}
            <div className="engage-profile__webcam-actions">
              <button type="button" className="engage-profile__webcam-btn engage-profile__webcam-btn--secondary" onClick={closeWebcam}>
                Cancel
              </button>
              <button
                type="button"
                className="engage-profile__webcam-btn engage-profile__webcam-btn--primary"
                onClick={handleCaptureWebcam}
                disabled={Boolean(webcamError)}
              >
                Use photo
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default EngageProfile;

